import type { DashboardData, Recurring } from "../../types";
import { formatMoney } from "../../lib/money-math";

/** A bill or regular income as the form edits it: strings for every field, as typed. */
export type BillDraft = {
  id?: string;
  expectedUpdatedAt?: number;
  type: "Expense" | "Income" | "Transfer";
  name: string;
  accountId: string;
  destinationAccountId: string;
  payeeId: string;
  /** Set when a new payee is kept locally until sync creates it. */
  payeeName?: string;
  category: string;
  subcategory: string;
  amount: string;
  currency: string;
  frequency: "weekly" | "monthly" | "yearly";
  startDate: string;
  endDate: string;
  reminderDaysBefore: string;
  /** The next due date when it differs from the default; empty means the default (one cycle after the base transaction). */
  nextDueDate: string;
  /** Create only: an existing transaction this bill is anchored to, recorded as its first paid
   * cycle. Once set, startDate/next due come from that transaction's own date, not a picked one. */
  sourceTransactionId?: string;
};

/** A new bill: monthly, from today, on the first active account. */
export function blankBill(data: DashboardData): BillDraft {
  const account = (data.accounts || []).find((a) => a.active !== false);
  return {
    type: "Expense",
    name: "",
    accountId: account?.id || "",
    destinationAccountId: "",
    payeeId: "",
    category: (data.entryOptions?.categories || [])[0] || "Uncategorized",
    subcategory: "",
    amount: "",
    currency: account?.currency || "MVR",
    frequency: "monthly",
    startDate: new Date().toISOString().slice(0, 10),
    endDate: "",
    reminderDaysBefore: "",
    nextDueDate: "",
  };
}

/** An existing bill, ready to edit. */
export function billToDraft(bill: Recurring): BillDraft {
  return {
    id: bill.id,
    expectedUpdatedAt: bill.updatedAt,
    type: bill.type === "Income" || bill.type === "Transfer" ? bill.type : "Expense",
    name: bill.name,
    accountId: bill.accountId,
    destinationAccountId: bill.destinationAccountId || "",
    payeeId: bill.counterpartyId || "",
    category: bill.category || "",
    subcategory: bill.subcategory || "",
    amount: bill.amount ? formatMoney(bill.amount) : "",
    currency: bill.currency,
    frequency: (["weekly", "monthly", "yearly"] as const).includes(bill.frequency as never)
      ? (bill.frequency as BillDraft["frequency"])
      : "monthly",
    startDate: bill.startDate,
    endDate: bill.endDate || "",
    reminderDaysBefore: bill.reminderDaysBefore != null ? String(bill.reminderDaysBefore) : "",
    nextDueDate: bill.nextDue.slice(0, 10),
  };
}

export const dayAfter = (date: string) => new Date(Date.parse(`${date}T00:00:00Z`) + 86400000).toISOString().slice(0, 10);
